import React, { useState, useEffect, useCallback } from 'react';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const OuraReadinessCard = () => {
  const [readinessData, setReadinessData] = useState([]);
  const [sleepData, setSleepData] = useState([]);
  const [loading, setLoading] = useState(true);

  const ouraUserId = localStorage.getItem('oura_user_id');

  const loadData = useCallback(async () => {
    if (!ouraUserId) return;
    setLoading(true);
    try {
      const today = new Date().toISOString().split('T')[0];
      const sevenDaysAgo = new Date(Date.now() - 7 * 86400000)
        .toISOString().split('T')[0];

      const [readinessRes, sleepRes] = await Promise.all([
        fetch(`${API_BASE}/api/oura/oauth/stored/readiness?user_id=${ouraUserId}&start_date=${sevenDaysAgo}&end_date=${today}`),
        fetch(`${API_BASE}/api/oura/oauth/stored/sleep?user_id=${ouraUserId}&start_date=${sevenDaysAgo}&end_date=${today}`)
      ]);

      if (readinessRes.ok) {
        const data = await readinessRes.json();
        setReadinessData(
          (data.records || []).sort((a, b) => b.date.localeCompare(a.date))
        );
      }
      if (sleepRes.ok) {
        const data = await sleepRes.json();
        setSleepData(
          (data.records || []).sort((a, b) => b.date.localeCompare(a.date))
        );
      }
    } catch (err) {
      console.error('Failed to load Oura data:', err);
    } finally {
      setLoading(false);
    }
  }, [ouraUserId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (!ouraUserId) return null;
  if (!loading && readinessData.length === 0 && sleepData.length === 0) return null;

  const todayReadiness = readinessData[0] || null;
  const todaySleep = sleepData[0] || null;

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Today';
    const date = new Date(dateStr + 'T00:00:00');
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (date.getTime() === today.getTime()) return 'Today';
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <div className="oura-readiness-card home-card">
      <div className="oura-readiness-card__header">
        <h3 className="card-title">Oura Readiness</h3>
        <span className="oura-readiness-card__date">
          {formatDate(todayReadiness?.date)}
        </span>
      </div>

      {loading ? (
        <div className="oura-readiness-card__loading">Loading readiness data...</div>
      ) : (
        <>
          <div className="oura-readiness-card__body">
            <div className="oura-readiness-card__score">
              <span className="score-value">
                {todayReadiness?.score != null
                  ? Math.round(todayReadiness.score)
                  : '--'}
              </span>
              <span className="score-label">Readiness</span>
            </div>

            <div className="oura-readiness-card__metrics">
              <div className="oura-metric">
                <span className="oura-metric__value">
                  {todayReadiness?.average_hrv != null
                    ? Math.round(todayReadiness.average_hrv)
                    : '--'}
                </span>
                <span className="oura-metric__label">HRV (ms)</span>
              </div>
              <div className="oura-metric">
                <span className="oura-metric__value">
                  {todayReadiness?.lowest_resting_hr != null
                    ? Math.round(todayReadiness.lowest_resting_hr)
                    : '--'}
                </span>
                <span className="oura-metric__label">Lowest RHR</span>
              </div>
              <div className="oura-metric">
                <span className="oura-metric__value">
                  {todaySleep?.score != null
                    ? Math.round(todaySleep.score)
                    : '--'}
                </span>
                <span className="oura-metric__label">Sleep Score</span>
              </div>
              <div className="oura-metric">
                <span className="oura-metric__value">
                  {todayReadiness?.temperature_deviation != null
                    ? `${todayReadiness.temperature_deviation > 0 ? '+' : ''}${todayReadiness.temperature_deviation.toFixed(1)}`
                    : '--'}
                </span>
                <span className="oura-metric__label">Temp Dev.</span>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default OuraReadinessCard;
